import { useQuery } from "@tanstack/react-query";

import { fetchDataGet } from "@/core/api/api-methods";
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  normalizePaged,
  type ListQueryParams,
  type Paged,
} from "@/core/api/pagination";
import { QUERY_KEYS } from "@/core/api/query-keys";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantStoreDto } from "@/features/merchant/api/dto";

/**
 * Points de vente d'un marchand
 * (`GET /MerchantStores/merchant/{merchantId}?page=&pageSize=` — réponse
 * `Paged` ; `normalizePaged` tolère un tableau brut transitoire).
 */
export const useMerchantStores = (
  merchantId: string,
  params: ListQueryParams = {},
  enabled = true
) => {
  const page = params.page ?? DEFAULT_PAGE;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  return useQuery({
    queryKey: QUERY_KEYS.MERCHANT_STORES_BY_MERCHANT(merchantId, {
      ...params,
      page,
      pageSize,
    }),
    queryFn: async () => {
      const data = await fetchDataGet<
        Paged<MerchantStoreDto> | MerchantStoreDto[] | MerchantStoreDto
      >({
        url: merchantEndpoints.merchantStores.byMerchant(merchantId),
        queryParams: {
          ...params.filters,
          page: String(page),
          pageSize: String(pageSize),
        },
      });
      return normalizePaged(data, pageSize);
    },
    retry: (failureCount, error) =>
      !/401/.test(error?.message ?? "") && failureCount < 1,
    enabled: enabled && Boolean(merchantId),
  });
};
